import { ESdChangeReason, ICommonSettings, IFileUploadPathSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigFileUploadPathSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingFileUploadPathSetting?: IFileUploadPathSetting;
}

export interface IConfigFileUploadPathSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFileUploadPathId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFileUploadPathSettings extends React.PureComponent<
  IConfigFileUploadPathSettingsProps,
  IConfigFileUploadPathSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFileUploadPathSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingFileUploadPathSetting: undefined
    };
  }

  componentDidMount() {
    this.loadFileUploadPathSettingSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFileUploadPathSettingsProps) {
    if (prevProps.editingFileUploadPathId && !this.props.editingFileUploadPathId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingFileUploadPathId !== this.props.editingFileUploadPathId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadFileUploadPathSettingSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadFileUploadPathSettingSettings();
    }
  }

  loadFileUploadPathSettingSettings = () => {
    if (!this.props.editingFileUploadPathId) return;

    const fileUploadPathSettings = _.cloneDeep(this.props.commonSettings?.fileUploadPathSettings);
    const editingFileUploadPathSetting = fileUploadPathSettings?.find(
      (setting) => setting.id === this.props.editingFileUploadPathId
    );
    if (editingFileUploadPathSetting)
      this.setState({ editingFileUploadPathSetting }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingFileUploadPathSetting);
      });
  };

  existingNameValidator = async (_rule, value) => {
    const fileUploadPathSettings = this.props.commonSettings?.fileUploadPathSettings;

    const isSettingAlreadyExist =
      fileUploadPathSettings?.some((x) => x.name === value && x.id !== this.props.editingFileUploadPathId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("File Upload Path Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    const obj: IFileUploadPathSetting = {
      id:
        this.props.editingFileUploadPathId && this.props.editingFileUploadPathId !== ""
          ? this.props.editingFileUploadPathId
          : Guid.create().toString(),
      name: data.name,
      fileUploadPath: data.fileUploadPath
    };
    if (this.props.editingFileUploadPathId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "fileUploadPathSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "fileUploadPathSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteFileUploadPathSetting = () => {
    if (!this.props.editingFileUploadPathId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFileUploadPathId || "",
      configKey: "fileUploadPathSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`File Upload Path setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  handleGenerateOTPSecret = () => {
    const secretKey = KMainFunctions.generateRandomString(32).replace(/[^A-Za-z2-7]/g, "");
    const fomrValues = { ...this.formRef.current?.getFieldsValue() };
    fomrValues.secretKey = secretKey;
    this.formRef.current?.setFieldsValue(fomrValues);
    this.formRef.current
      ?.validateFields()
      .then(() => {})
      .catch(() => {});
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="File Upload Path Setting"
        secondHeader="File Upload Path Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFileUploadPathId ? this.deleteFileUploadPathSetting : undefined}
            deleteTestId="fileUploadPathSettingDeleteButton"
            submitTestId="fileUploadPathSettingCreateButton"
            submitLabel={this.props.editingFileUploadPathId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFileUploadPathSettingsForm"
          />
        }
      >
        <Form
          id="configFileUploadPathSettingsForm"
          name="fileUploadPathSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_fileuploadpathsettingpop_up_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_fileuploadpathsettingpop_up_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a File Upload Path Setting Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "File Upload Path Setting Name is invalid." },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="fileUploadPathSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField
                label="File Upload Path"
                required
                tooltipContentKey="appsettings_fileuploadpathsettingpop_up_fileuploadpath"
              >
                <Form.Item
                  name="fileUploadPath"
                  rules={[{ whitespace: true, required: true, message: "Please enter a File Upload Path." }]}
                >
                  <KuikaInput size="md" data-testid="fileUploadPathSettingPathInput" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}
